import React, { createContext, useContext, useState, useCallback } from 'react';
import { ProductType } from 'screens/SelectServices/schemas';
import { ProductDetails } from 'screens/SelectServices/utils';

export type MNS_Package = 'essential' | 'enhanced';

export type SelectedPackages = {
  essential: boolean;
  enhanced: boolean;
};

interface SingleServiceManagerContextType {
  pinnedServices: Record<string, ProductDetails | null>;
  pinService: (id: string, type: string, product: ProductDetails) => void;
  unpinService: (id: string, type: string) => void;
  isUnpinning: boolean | null;
  productMap: Record<
    string,
    {
      product: ProductType;
      mns: SelectedPackages | null;
    }
  >;
  handleProductChange: (
    id: string,
    type: string,
    isPinned: boolean,
    product: ProductType,
    mns: SelectedPackages | null,
  ) => void;

  selectedValuesMap: Record<string, Record<string, string>>;
  setSelectedValuesMap: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>>>
  >;
  updateSelectedValues: (
    id: string,
    type: string,
    isPinnedSummary: boolean,
    newSelectedValues: Record<string, string>,
  ) => void;
  resetSelectedValues: (
    id: string,
    type: string,
    isPinnedSummary: boolean,
  ) => void;
  setProductMap: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          product: ProductType;
          mns: SelectedPackages | null;
        }
      >
    >
  >;
}

const SingleServiceManagerContext = createContext<
  SingleServiceManagerContextType | undefined
>(undefined);

export const SingleServiceManagerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isUnpinning, setIsUnpinning] = useState<boolean | null>(null);
  const [pinnedServices, setPinnedServices] = useState<
    Record<string, ProductDetails | null>
  >({});
  const [productMap, setProductMap] = useState<
    Record<
      string,
      {
        product: ProductType;
        mns: SelectedPackages | null;
      }
    >
  >({});
  const [selectedValuesMap, setSelectedValuesMap] = useState<
    Record<string, Record<string, string>>
  >({});

  const updateSelectedValues = useCallback(
    (
      id: string,
      type: string,
      isPinnedSummary: boolean,
      newSelectedValues: Record<string, string>,
    ) => {
      const key = `${id}_${type}_${isPinnedSummary}`;

      setSelectedValuesMap((prev) => {
        const currentValues = prev[key] || {};
        const updated = {
          ...prev,
          [key]: { ...currentValues, ...newSelectedValues },
        };
        return updated;
      });
    },
    [],
  );

  const resetSelectedValues = useCallback(
    (id: string, type: string, isPinnedSummary: boolean) => {
      const key = `${id}_${type}_${isPinnedSummary}`;
      setSelectedValuesMap((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    },
    [],
  );

  const handleProductChange = useCallback(
    (
      id: string,
      type: string,
      isPinned: boolean,
      product: ProductType,
      mns: SelectedPackages | null,
    ) => {
      setProductMap((prev) => ({
        ...prev,
        [[id, type, isPinned].join(',')]: { product, mns },
      }));
    },
    [],
  );

  const pinService = useCallback(
    (id: string, type: string, product: ProductDetails) => {
      const compositeKey = `${id}_${type}`;
      setPinnedServices((prev) => ({
        ...prev,
        [compositeKey]: product,
      }));
    },
    [],
  );

  const unpinService = useCallback(
    (id: string, type: string) => {
      const compositeKey = `${id}_${type}`;
      const pinnedKey = `${id}_${type}_true`;
      setTimeout(() => {
        setPinnedServices((prev) => {
          const { [compositeKey]: pinnedProduct, ...rest } = prev;
          if (pinnedProduct) {
            updateSelectedValues(
              id,
              type,
              false,
              selectedValuesMap[pinnedKey] || {},
            );
          }
          return rest;
        });

        setPinnedServices((prev) => {
          const { [compositeKey]: _, ...rest } = prev;
          return rest;
        });
        setIsUnpinning(false);
      }, 350);

      setIsUnpinning(true);
    },
    [selectedValuesMap, updateSelectedValues],
  );

  return (
    <SingleServiceManagerContext.Provider
      value={{
        pinnedServices,
        pinService,
        unpinService,
        isUnpinning,
        productMap,
        handleProductChange,
        selectedValuesMap,
        setSelectedValuesMap,
        updateSelectedValues,
        resetSelectedValues,
        setProductMap,
      }}
    >
      {children}
    </SingleServiceManagerContext.Provider>
  );
};

export const useSingleServiceManager = () => {
  const context = useContext(SingleServiceManagerContext);
  if (!context) {
    throw new Error(
      'useSingleServiceManager must be used within a SingleServiceManagerProvider',
    );
  }
  return context;
};
